// frontend/src/components/layout/Layout.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { ChevronUp, Shield, Heart } from 'lucide-react';

// =========================================================
// CONSTANTS
// =========================================================
const SIDEBAR_WIDTH = 256; // px (w-64)
const MOBILE_BREAKPOINT = 768;
const SCROLL_THRESHOLD = 10;
const SCROLL_TOP_THRESHOLD = 400;
const SIDEBAR_PREF_KEY = 'sidebar_collapsed';

// =========================================================
// HOOKS
// =========================================================
function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e) => setMatches(e.matches);
        // Modern API
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else mql.addListener(handler); // Safari < 14
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else mql.removeListener(handler);
        };
    }, [query]);

    return matches;
}

function useScrollPosition() {
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        let rafId = null;
        const onScroll = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                setScrollY(window.scrollY);
                rafId = null;
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll(); // initial
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, []);

    return scrollY;
}

function useBodyScrollLock(locked) {
    useEffect(() => {
        if (!locked) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = original; };
    }, [locked]);
}

// =========================================================
// MAIN COMPONENT
// =========================================================
export default function Layout() {
    const location = useLocation();

    const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const scrollY = useScrollPosition();

    // Desktop collapse preference (persisted)
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem(SIDEBAR_PREF_KEY) === 'true';
        } catch { return false; }
    });

    // Mobile drawer open state
    const [mobileOpen, setMobileOpen] = useState(false);

    // Derived
    const scrolled = scrollY > SCROLL_THRESHOLD;
    const showScrollTop = scrollY > SCROLL_TOP_THRESHOLD;

    // ---------- Persist collapse preference ----------
    useEffect(() => {
        try { localStorage.setItem(SIDEBAR_PREF_KEY, String(collapsed)); } catch { /* ignore */ }
    }, [collapsed]);

    // ---------- Toggle ----------
    const toggleSidebar = useCallback(() => {
        if (isMobile) setMobileOpen((v) => !v);
        else setCollapsed((v) => !v);
    }, [isMobile]);

    const closeMobile = useCallback(() => setMobileOpen(false), []);

    // ---------- Close mobile drawer on route change ----------
    useEffect(() => {
        if (isMobile && mobileOpen) setMobileOpen(false);
        // eslint-disable-next-line
    }, [location.pathname]);

    // ---------- Close mobile drawer on resize to desktop ----------
    useEffect(() => {
        if (!isMobile && mobileOpen) setMobileOpen(false);
    }, [isMobile, mobileOpen]);

    // ---------- Esc closes mobile drawer ----------
    useEffect(() => {
        if (!mobileOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') closeMobile(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mobileOpen, closeMobile]);

    // ---------- Lock body scroll while mobile drawer is open ----------
    useBodyScrollLock(isMobile && mobileOpen);

    // ---------- Scroll to top ----------
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    // ---------- Sidebar translate class ----------
    const sidebarTransform = isMobile
        ? (mobileOpen ? 'translate-x-0' : '-translate-x-full')
        : 'translate-x-0';

    return (
        <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-slate-900 transition-colors duration-300">

            {/* ============ SKIP LINK (a11y) ============ */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-600 focus:text-white focus:rounded-lg focus:shadow-xl"
            >
                Skip to content
            </a>

            {/* ============ SIDEBAR ============ */}
            <aside
                className={`fixed inset-y-0 left-0 z-40 bg-white dark:bg-slate-800 shadow-2xl dark:shadow-2xl dark:shadow-black/30 transform transition-transform duration-300 ease-in-out ${sidebarTransform} md:relative md:translate-x-0 md:z-0`}
                style={{ width: isMobile ? SIDEBAR_WIDTH : 'auto' }}
                role="navigation"
                aria-label="Main navigation"
                aria-hidden={isMobile && !mobileOpen}
            >
                <Sidebar
                    collapsed={!isMobile && collapsed}
                    onToggleCollapse={() => setCollapsed((v) => !v)}
                />
            </aside>

            {/* ============ MOBILE BACKDROP ============ */}
            {isMobile && mobileOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/60 dark:bg-black/80 backdrop-blur-sm md:hidden animate-fadeIn"
                    onClick={closeMobile}
                    aria-hidden="true"
                />
            )}

            {/* ============ MAIN AREA ============ */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <header
                    role="banner"
                    className={`sticky top-0 z-20 transition-shadow duration-300 ${scrolled
                            ? 'shadow-lg dark:shadow-black/40'
                            : 'shadow-sm dark:shadow-black/10'
                        }`}
                >
                    <Header onToggleSidebar={toggleSidebar} isMobile={isMobile} />
                </header>

                {/* Main scroll area */}
                <main
                    id="main-content"
                    role="main"
                    className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-gradient-to-br from-gray-50 via-white to-brand-500/5 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300"
                >
                    <div className="max-w-7xl mx-auto">
                        <Outlet />
                    </div>

                    <Footer />
                </main>

                {/* Scroll to top */}
                <ScrollTopButton visible={showScrollTop} onClick={scrollToTop} />

                {/* Mobile live-connection pill */}
                <MobileConnectionPill />
            </div>
        </div>
    );
}

// =========================================================
// SUB-COMPONENTS
// =========================================================

function Footer() {
    const links = [
        { to: '/privacy', label: 'Privacy Policy' },
        { to: '/terms', label: 'Terms of Service' },
        { to: '/support', label: 'Support' },
    ];

    return (
        <footer
            role="contentinfo"
            className="max-w-7xl mx-auto mt-12 pt-6 border-t border-gray-200 dark:border-slate-700/50"
        >
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-gray-400 dark:text-slate-500">
                <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-brand-600 dark:text-green-400" />
                    <p>� {new Date().getFullYear()} Mayfair Insurance. All rights reserved.</p>
                </div>

                <nav className="flex items-center gap-4" aria-label="Footer navigation">
                    {links.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            className="hover:text-gray-600 dark:hover:text-slate-300 transition-colors hover:underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-brand-500/40 rounded"
                        >
                            {l.label}
                        </Link>
                    ))}
                   
                </nav>
            </div>
        </footer>
    );
}

function ScrollTopButton({ visible, onClick }) {
    return (
        <button
            onClick={onClick}
            aria-label="Scroll to top"
            className={`fixed bottom-8 right-8 z-30 p-3 bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-brand-600 ${visible
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 translate-y-4 pointer-events-none'
                }`}
        >
            <ChevronUp className="w-5 h-5" />
        </button>
    );
}

function MobileConnectionPill() {
    // Subscribes to the same navigator.onLine signal used app-wide
    const [online, setOnline] = useState(() =>
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );

    useEffect(() => {
        const on = () => setOnline(true);
        const off = () => setOnline(false);
        window.addEventListener('online', on);
        window.addEventListener('offline', off);
        return () => {
            window.removeEventListener('online', on);
            window.removeEventListener('offline', off);
        };
    }, []);

    return (
        <div className="fixed bottom-4 left-4 z-30 md:hidden">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 dark:border-slate-700 text-xs">
                <span
                    className={`w-2 h-2 rounded-full ${online ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                        }`}
                />
                <span className="text-gray-600 dark:text-slate-300">
                    {online ? 'Live' : 'Offline'}
                </span>
            </div>
        </div>
    );
}